import React from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './PreviewGroup.css';

// Format text by replacing underscores with spaces and capitalizing first letter of each word
const formatText = text => {
  if (!text) return '';

  // Replace underscores with spaces
  const textWithSpaces = text.replace(/_/g, ' ');

  // Capitalize the first letter of each word
  return textWithSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PreviewGroupSchedule = ({ open, onClose, session }) => {
  // Get group session data directly if not provided
  const groupSession = session;

  if (!groupSession) return null;

  // Transform availability data if it's in API format
  const transformAvailability = () => {
    if (!groupSession.availability) return [];

    // Check if the availability is in the API format (with days array)
    const isApiFormat =
      groupSession.availability.length > 0 && Array.isArray(groupSession.availability[0].days);

    if (isApiFormat) {
      // Create a map to group time slots by day
      const dayToTimeSlotsMap = {};

      // Process each availability slot
      groupSession.availability.forEach(slot => {
        slot.days.forEach(day => {
          // Initialize array for this day if it doesn't exist
          if (!dayToTimeSlotsMap[day]) {
            dayToTimeSlotsMap[day] = [];
          }

          // Add time slot to this day
          dayToTimeSlotsMap[day].push({
            start_time: slot.startTime,
            end_time: slot.endTime,
          });
        });
      });

      // Convert the map to the expected format
      const transformed = Object.entries(dayToTimeSlotsMap).map(([day, timeSlots]) => ({
        day_of_week: day,
        time_slots: timeSlots,
      }));

      return transformed;
    }

    // If the data is already in the expected format with days and time slots
    // We need to ensure time slots are properly grouped by day
    if (groupSession.availability.length > 0) {
      // Create a map of days to time slots
      const dayToTimeSlotsMap = {};

      groupSession.availability.forEach(item => {
        const day = item.day_of_week;

        if (!dayToTimeSlotsMap[day]) {
          dayToTimeSlotsMap[day] = [];
        }

        if (item.time_slots && Array.isArray(item.time_slots)) {
          dayToTimeSlotsMap[day].push(...item.time_slots);
        }
      });

      // Convert back to array format
      return Object.entries(dayToTimeSlotsMap).map(([day, timeSlots]) => ({
        day_of_week: day,
        time_slots: timeSlots,
      }));
    }

    // Return as is if already in the expected format
    return groupSession.availability;
  };

  const availability = transformAvailability();

  // Use tags from the session data or an empty array if not available
  const tags = groupSession.tags || [];

  // Format the level of teaching
  const formattedLevel =
    groupSession.level_of_teaching === 'all'
      ? 'All Levels'
      : formatText(groupSession.level_of_teaching || groupSession.level || 'None');

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="preview-modal-title"
      className="group-preview-modal"
    >
      <Box className="group-preview-modal-content">
        <Box className="group-preview-modal-header">
          <Typography variant="h6" className="group-preview-title">
            Preview
          </Typography>
          <IconButton onClick={onClose} className="group-close-button" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="group-preview-modal-body">
          <Box className="group-preview-section">
            <Typography className="group-section-title">Subject</Typography>
            <Box className="group-section-content">
              <Typography className="group-section-value">
                {groupSession.subject === 'all'
                  ? 'All'
                  : formatText(groupSession.subject || 'Not provided')}
              </Typography>
            </Box>
          </Box>

          <Box className="group-preview-section">
            <Typography className="group-section-title">Tags</Typography>
            <Box className="group-section-content group-tags-container">
              {tags.length > 0 ? (
                tags.map((tag, index) => (
                  <Box key={index} className="group-tag">
                    <Typography className="group-tag-text">{tag}</Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                  No tags available
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="group-preview-section">
            <Typography className="group-section-title">Level to teach</Typography>
            <Box className="group-section-content">
              <Typography className="group-section-value">{formattedLevel}</Typography>
            </Box>
          </Box>

          <Box className="group-preview-section">
            <Typography className="group-section-title">Session Type</Typography>
            <Box className="group-section-content">
              <Typography className="group-text">Group Sessions</Typography>
            </Box>
          </Box>

          <Box className="group-preview-section">
            <Typography className="group-section-title">Days & Time</Typography>
            <Box className="group-section-content group-days-time-section">
              {availability && availability.length > 0 ? (
                availability.map((day, index) => (
                  <Box key={index} className="group-day-row">
                    <Typography className="group-day-name">{day.day_of_week}</Typography>
                    <Box className="group-time-slots-container">
                      {(day.time_slots || []).map((slot, slotIndex) => (
                        <Box key={slotIndex} className="group-time-slot">
                          {`${formatTime(slot.start_time)} - ${formatTime(slot.end_time)}`}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                  No scheduled time slots available
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

// Helper function to format time
const formatTime = time => {
  if (!time) return '';

  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);

  let formattedHour = hour;
  let period = 'AM';

  if (hour >= 12) {
    period = 'PM';
    if (hour > 12) {
      formattedHour = hour - 12;
    }
  }

  if (hour === 0) {
    formattedHour = 12;
  }

  return `${formattedHour.toString().padStart(2, '0')}:${minutes} ${period}`;
};

export default PreviewGroupSchedule;
